import { Component, OnDestroy } from '@angular/core';
import { MatSidenav } from '@angular/material/sidenav';
import { Router } from '@angular/router';
import { DEFAULT_INTERRUPTSOURCES, Idle, IdleExpiry } from '@ng-idle/core';
import { Keepalive } from '@ng-idle/keepalive';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { AppState } from 'src/app/app.reducer';
import { loadUser, unsetUser } from 'src/app/auth/auth.actions';
import { CognitoService } from 'src/app/auth/services/cognito.service';
import { UserRoles } from 'src/app/shared/enums/roles-enum';
import { SpinnerService } from 'src/app/shared/services/spinner.service';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-retail-home',
  templateUrl: './retail-home.component.html',
  styleUrls: ['./retail-home.component.sass']
})
export class RetailHomeComponent implements OnDestroy {
  isEnabledUser: boolean = false;
  userName: string = '';
  userRole: UserRoles = UserRoles.aguacate_retail_client;
  rolesEnum = UserRoles;
  subscription: Subscription = new Subscription();
  canSeePayers : number = 0;
  private numberOfSeconds: number = 15;

  constructor(
    private store: Store<AppState>,
    private auth: CognitoService,
    private spinner: SpinnerService,
    private router: Router,
    private _idle: Idle,
    private idleExpiry: IdleExpiry,
    private keepAlive: Keepalive,
    private cognitoService: CognitoService,

  ) {
    this.setIdle();

    this.subscription.add(
      this.store.select('auth').subscribe(async ({ user, isLogged }) => {
        this.canSeePayers = user?.canAddPayers ? user?.canAddPayers : 0;
        const cognitoUser = await this.auth.getUser();
        const cognitoSession = await this.auth.getSession();
        let userGroup;
        if(cognitoSession){
          userGroup =
          cognitoSession['accessToken']['payload']['cognito:groups'][0];
          this.userName = cognitoUser.attributes['email'];
          this.userRole = userGroup;
        }
        if (!isLogged) {
          this.store.dispatch(
            loadUser({ email: this.userName, role: userGroup })
          );
          return;
        }
        if (user && Object.keys(user).length !== 0 && user !== null) {
          this.userName = `${user.nombre} ${user.apellidos}`;
          this.isEnabledUser = true;
        }
      })
    );
  }

  private async setIdle(){
    const user = await this.cognitoService.getUser();
    // Puede venir null si la sesion ya se cerro (tope de 12 h, por ejemplo).
    const role = user?.attributes?.['custom:user-group'];
    // Mismo valor en escritorio y en celular.
    if(role === UserRoles.aguacate_retail_cashier.toString()){
      this.numberOfSeconds = 28800; // 8 h
    }else{
      this.numberOfSeconds=3600; // 1 h
    }

    // Inactividad vencida mientras la pestana estuvo cerrada: se corta antes
    // de dejar corriendo el contador, que si no regalaria un periodo entero.
    if (this.idleExpiry.isExpired()) {
      this.logout();
      return;
    }

    this._idle.setIdle(this.numberOfSeconds);
    this._idle.setTimeout(5);
    this._idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);

    this._idle.onIdleEnd.subscribe(() => {
      this.reset();
    });
    this._idle.onIdleStart.subscribe(()=>{
    })
    this._idle.onTimeout.subscribe(async () => {
      const isAuthenticated = await this.cognitoService.isAuthenticated();

      if(isAuthenticated){
        let timerInterval: number | undefined;
        const result = await Swal.fire({
          icon: 'question',
          timerProgressBar: true,
          html: 'Llevas mucho tiempo inactivo, la sesión se cerrará en <b></b> segundos.',
          didOpen: () => {
            let b = Swal.getHtmlContainer()?.querySelector('b');
            timerInterval = window.setInterval(() => {
              if (b) {
                b.textContent =
                  String(Math.floor((Swal.getTimerLeft() || 0) / 1000)) ||'';
                }

              clearInterval(timerInterval);
            }, 1000);
          },
          willClose: () => {
            clearInterval(timerInterval);
             },
          timer: 20000,
          title: '¿Sigues ahí?',
          text: `Llevas más de ${this.idleLabel()} sin actividad, se cerrará tu sesión`,
          showCancelButton: true,
          showConfirmButton: true,
          allowOutsideClick: false,
          cancelButtonText: 'Cerrar sesión',
          confirmButtonText: 'Sigo aquí',
          confirmButtonColor: '#1e5a3f',
        });
        if(result.isConfirmed){
        }
        if(result.isDismissed && result.dismiss?.toString() === 'timer'){
          this.logout();
        }
        if(result.isDismissed && result.dismiss?.toString() ==='cancel'){
          this.logout();
        }

        this.reset();
      }else{
        this.reset();
      }


    });
    this.startIdleWatch();
    this.keepAlive.interval(15);
  }

  /**
   * Arranca el contador de inactividad retomando el que ya venía corriendo.
   * `watch()` a secas reescribe la expiración y cada recarga lo ponía en cero.
   * El caso "ya venció con la pestaña cerrada" se resuelve antes, en setIdle().
   */
  private startIdleWatch(): void {
    const last = this.idleExpiry.last();

    if (!last) {
      this._idle.watch();
      return;
    }

    this._idle.watch(true);

    // @ng-idle recién comprueba un período completo más adelante, aunque la
    // expiración que retomamos venza mucho antes: hay que vigilarla aparte.
    this.watchResumedIdleExpiry(last.getTime() - Date.now());
  }

  private resumedIdleHandle: any = null;

  private watchResumedIdleExpiry(remainingMs: number): void {
    clearTimeout(this.resumedIdleHandle);
    this.resumedIdleHandle = setTimeout(
      () => {
        if (this.idleExpiry.isExpired()) {
          this.logout();
        }
      },
      Math.max(0, remainingMs) + 1000,
    );
  }

  /** Etiqueta legible del período de inactividad configurado. */
  private idleLabel(): string {
    const minutes = Math.round(this.numberOfSeconds / 60);
    if (minutes < 60) return `${minutes} minutos`;
    const hours = Math.round(minutes / 60);
    return hours === 1 ? '1 hora' : `${hours} horas`;
  }
  private logout(): void{
    const ref = this.spinner.start();
    this.cognitoService.signOut().then(()=> {
      this.router.navigate(['/auth/login']);
      this.spinner.stop(ref);
    })
  }
  async listenIdle(){
    const time = await this.cognitoService.getSession();
   }

   reset() {
     this._idle.watch();
   }


  ngOnDestroy(): void {
    this.subscription.unsubscribe();
    clearTimeout(this.resumedIdleHandle);
  }
  signOut() {
    Swal.fire({
      iconHtml: '<img src="assets/icons/question.svg" style="width: 80px; height: 80px; margin-top:20px"/>',
      customClass: {
        icon: 'no-border animate-bounce'
      },
      title: 'Cerrar sesión',
      text: '¿Está seguro que desea cerrar la sesión?',
      showCancelButton: true,
      cancelButtonText: 'No',
      showDenyButton: false,
      confirmButtonText: 'Sí',
      confirmButtonColor: '#1e5a3f',

    }).then(async (result: { isConfirmed: any; })=>{
      if(result.isConfirmed){
        let spinnerRef = this.spinner.start();
        await this.store.dispatch(unsetUser());
        this.auth
          .signOut()
          .then(() => {
            this.router.navigate(['auth/ac']);
            this.spinner.stop(spinnerRef);
          })
          .catch(() => {
            this.spinner.stop(spinnerRef);
          });
      }
    });

  }

  closeSidenav(sidenav: MatSidenav) {
    sidenav.close();
  }

  redirectToExternalLink(): void {
    window.open('https://wa.me/56954838838?text=Hola,%20necesito%20asistencia', '_blank');
  }

}
